import { useState } from "react";
import { authPost, errorMessage } from "../lib/authApi.ts";

// The forgot-password screen. It answers the same way for every address, as
// the server does, so it discloses nothing about which are registered.
// See ADR-0042.

/**
 * Asks for an email and requests a password-reset link that lands on
 * `/reset-password`. Rendered by the public entry at `/forgot-password`.
 */
export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setState("sending");
    try {
      await authPost("/request-password-reset", { email, redirectTo: "/reset-password" });
      setState("sent");
    } catch (err) {
      setError(errorMessage(err));
      setState("idle");
    }
  }

  if (state === "sent") {
    return (
      <div className="auth-card">
        <h1>Check your inbox</h1>
        <p className="lede" role="status">
          If <strong>{email}</strong> has an account, a reset link is on its way.
          It is good for fifteen minutes and can be used once.
        </p>
        <p className="hint">
          <a href="/login">Back to sign in</a>
        </p>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <form onSubmit={onSubmit} aria-label="Forgot password">
        <h1>Forgot your password?</h1>
        <p className="lede">
          Type your email and we will send a link to set a new one.
        </p>
        <div className="field">
          <label htmlFor="forgot-email">Email</label>
          <input
            id="forgot-email"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {error ? (
          <div className="notice bad" role="alert">
            <div>{error}</div>
          </div>
        ) : null}
        <div className="btn-row">
          <button type="submit" className="btn primary lg block" disabled={state === "sending"}>
            {state === "sending" ? "Sending…" : "Send reset link"}
          </button>
        </div>
        <p className="hint">
          <a href="/login">Back to sign in</a>
        </p>
      </form>
    </div>
  );
}
